import React, { useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import PlanningItemService from '../../../Services/PlanningItemService.js';
import PlanningAssignmentService from '../../../Services/PlanningAssignmentService';
import PlanningItemMultiEditWindowDescription from './PlanningItemMultiEditWindowDescription';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import PlanningAssignmentEditWindowDescription from './PlanningAssignmentEditWindowDescription';
import PlanningDetailsItemService from '../../../Services/PlanningDetailsItemService';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import PlanningItemStatusUpdateWindowDescription from './PlanningItemStatusUpdateWindowDescription';
import AddFieldworkPeriodsWindowDescription from './AddFieldworkPeriodsWindowDescription';
import ButtonRow from '../../../Elements/ButtonRow';
import InfoBox from '../../../Elements/InfoBox';
import ContextStrip from '../../../Elements/DesignSystem/ContextStrip';
import Breadcrumb from '../../../Elements/DesignSystem/Breadcrumb';
import Chip from '../../../Elements/DesignSystem/Chip';
import PlanningItem from '../../../Domain/PlanningItem';
import ErrorMessage from '../../../Elements/ErrorMessage';
import PlanningMain from '../PlanningMain';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import PlanningSearchParams from './PlanningSearchParams';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import Loading from '../../../Elements/loading';
import PlanningExportWindowDescription from '../Export/PlanningExportWindowDescription';
import PlanningExportItem from '../../../Domain/PlanningExportItem';
import PlanningExportLevelsEnum from '../../../Enums/PlanningExportLevelsEnum';

// A scheduler / account-manager save returns as soon as it is written, but the grid reads the
// planning temp tables, which are rebuilt in the background afterwards. Reloading straight away
// therefore re-reads the same figures the user is waiting to see replaced — which is why an
// applied Bulk User Upload used to need a manual Refresh. Watch the rebuild out instead.
const REFRESH_POLL_INTERVAL_MS = 4000;
// Long enough for a big opportunity's rebuild; past it, reload anyway rather than poll for ever.
const REFRESH_POLL_GIVE_UP_MS = 240000;

function PlanningList(props) {
  const [serviceId] = useState(() => props.serviceId || new URLSearchParams(window.location.search).get('ServiceId'));
  const [searchItem, setSearchItem] = useState(() => new PlanningSearchParams());
  const [planningItemsList, setPlanningItemsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  const [assignmentEditItems, setAssignmentEditItems] = useState([]);
  const [multiEditItem, setMultiEditItem] = useState(null);
  const [refreshPending, setRefreshPending] = useState(false);
  const [searchParams] = useState({
    PlanningId: '',
    CountryName: '',
    PricingOfficeName: '',
  });

  const listRef = useRef(planningItemsList);
  listRef.current = planningItemsList;

  const refresh = () => {
    setSearchItem((prev) => ({ ...prev }));
  };

  useEffect(() => {
    PlanningItem.clickOnSelect = (item) => {
      item.Selected = !item.Selected;
      setPlanningItemsList([...listRef.current]);
    };
    PlanningItem.clickOnGlobalSelect = () => {
      const list = listRef.current;
      const newSelected = list.some((item) => item.Selected === false);
      const templist = [...list];
      templist.forEach((c) => {
        c.Selected = newSelected;
      });
      setPlanningItemsList(templist);
    };
    PlanningItem.selectedStatus = () => !listRef.current.some((x) => x.Selected === false);
    PlanningItem.openAssignmentEditor = (item) => {
      setAssignmentEditItems([item]);
      setShownPanel(ShownPanelsEnum.PlanningAssignmentEdit);
    };
  }, []);

  useEffect(() => {
    const itemsUnderRefreshing = planningItemsList.filter((pi) => pi.UpcomingChanges > 0);
    if (itemsUnderRefreshing.length === 0) return undefined;
    const timeoutId = window.setTimeout(refresh, 60000);
    return () => window.clearTimeout(timeoutId);
  }, [planningItemsList]);

  // Wait out the background temp-table rebuild an assignment save queued, then reload the grid
  // once — so the change the user just applied appears on its own. The server counts the job
  // before it answers the save, so the first poll can go out immediately: a rebuild that ran
  // synchronously (the service-wide account manager path) reports "done" straight away and the
  // grid reloads without the wait.
  useEffect(() => {
    if (!refreshPending) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    let timeoutId = 0;
    const stopWaiting = () => {
      if (cancelled) return;
      setRefreshPending(false);
      refresh();
    };
    const poll = () => {
      PlanningAssignmentService.IsRefreshPending()
        .then((pending) => {
          if (cancelled) return;
          if (pending && Date.now() - startedAt < REFRESH_POLL_GIVE_UP_MS) {
            timeoutId = window.setTimeout(poll, REFRESH_POLL_INTERVAL_MS);
            return;
          }
          stopWaiting();
        })
        // The rebuild itself is not in doubt, only our ability to watch it — reload once and let
        // the user take it from there rather than keep polling an endpoint that is answering with
        // an error.
        .catch(stopWaiting);
    };
    poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [refreshPending]);

  const getEditedPlanningItems = () =>
    planningItemsList.filter(
      (item) =>
        item.Selected &&
        item.PlanningId.toString().includes(searchParams.PlanningId) &&
        item.CountryName.toString().toLowerCase().includes(searchParams.CountryName.toString().toLowerCase()) &&
        item.PricingOfficeName.includes(searchParams.PricingOfficeName)
    );

  const showPanel = (panelName) => {
    if (panelName === ShownPanelsEnum.Refresh) {
      refresh();
      setShownPanel(ShownPanelsEnum.None);
      return;
    }
    setShownPanel((prev) => (panelName === prev ? ShownPanelsEnum.None : panelName));
  };

  const maxNumberOfPlanningDetails = () => Math.max(...planningItemsList.map((item) => item.NumberOfPlanningDetails));

  const getSelectedCountriesWithAssignmentNumbers = () =>
    getEditedPlanningItems().map((item) => ({
      pricingId: item.PricingId,
      country: item.CountryName,
      numberOfContractedAssignments: Math.floor(item.NumberOfContractedAssignments / item.NumberOfPlanningDetails),
      numberOfPlannedAssignments: Math.floor(item.NumberOfPlannedAssignments / item.NumberOfPlanningDetails),
      startDate: item.OpportunityActualStartOn,
      endDate: item.OpportunityActualEndOn,
    }));

  const renderContextStrip = () => {
    const first = planningItemsList[0];
    const itemsUnderRefreshing = planningItemsList.filter((pi) => pi.UpcomingChanges > 0);
    const actualMinute = new Date().getMinutes();

    return (
      <ContextStrip>
        <Breadcrumb
          items={[
            {
              text: 'Planning — Services',
              title: 'Planning - Services',
              link: <PlanningMain />,
              url: 'planning',
            },
            { text: 'Planning Items', title: 'Planning - Planning Items', isCurrentPage: true },
          ]}
        />
        {first && (
          <div className='meta'>
            <Chip label='Client'>
              {first.ClientName} · {first.ClientId}
            </Chip>
            <Chip label='Industry'>{first.ClientIndustry}</Chip>
            <Chip label='Office'>{first.ClientOfficeName}</Chip>
            <Chip label='Opportunity'>
              {first.OpportunityName} · {first.OpportunityId}
            </Chip>
            <Chip label='Opportunity office'>{first.OpportunityOfficeName}</Chip>
            <Chip label='Service'>
              {first.ServiceName} · {first.ServiceId}
            </Chip>
            <Chip label='Cycles/year'>{first.NumCyclesPerYear}</Chip>
            <Chip label='Age'>{first.ServiceAge}</Chip>
          </div>
        )}
        {(refreshPending || itemsUnderRefreshing.length > 0) && (
          <div className='context-end'>
            {refreshPending && (
              <InfoBox
                title='Applying your changes'
                text='The planning grid is rebuilding with the scheduler and account manager changes you saved. This page updates itself when it is done — no need to refresh.'
                rotated='-'
              />
            )}
            {itemsUnderRefreshing.length > 0 && (
              <InfoBox
                title='Warning!'
                text={`Some of the following plannings are waiting for refreshing. The next refreshing will run within an hour. (Approx.: ${
                  65 - actualMinute
                } min.)`}
                rotated='+'
              />
            )}
          </div>
        )}
      </ContextStrip>
    );
  };

  const renderAssignmentEditPanel = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={new PlanningAssignmentEditWindowDescription.itemType(assignmentEditItems)}
      windowDescription={PlanningAssignmentEditWindowDescription}
      // Same background rebuild as the bulk upload, so wait it out rather than reloading into the
      // figures the save has not reached yet.
      clickOnSave={() => {
        showPanel(ShownPanelsEnum.None);
        setRefreshPending(true);
      }}
      clickOnCancel={() => showPanel(ShownPanelsEnum.None)}
      errorCode={'PA_EDIT'}
    />
  );

  const renderMultiEditPanel = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={multiEditItem}
      windowDescription={PlanningItemMultiEditWindowDescription}
      approveBeforeSaveCheck={PlanningItemMultiEditWindowDescription.approveBeforeSaveCheck}
      approveBeforeSaveMessage={PlanningItemMultiEditWindowDescription.approveBeforeSaveMessage}
      clickOnSave={() => showPanel(ShownPanelsEnum.None)}
      clickOnCancel={() => showPanel(ShownPanelsEnum.None)}
      errorCode={'PL125'}
    />
  );

  const renderMassStatusUpdate = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={
        new PlanningItemStatusUpdateWindowDescription.itemType(getEditedPlanningItems(), maxNumberOfPlanningDetails())
      }
      windowDescription={PlanningItemStatusUpdateWindowDescription}
      approveBeforeSaveCheck={PlanningItemStatusUpdateWindowDescription.approveBeforeSaveCheck}
      approveBeforeSaveMessage={PlanningItemStatusUpdateWindowDescription.approveBeforeSaveMessage}
      clickOnSave={() => {
        showPanel(ShownPanelsEnum.None);
        refresh();
      }}
      clickOnCancel={() => showPanel(ShownPanelsEnum.None)}
      errorCode={'PL143'}
    />
  );

  const renderExportPanel = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={
        new PlanningExportItem({
          Level: PlanningExportLevelsEnum.Item,
          SelectedIds: getEditedPlanningItems().map((x) => x.PlanningId),
        })
      }
      windowDescription={PlanningExportWindowDescription.forLevel(PlanningExportLevelsEnum.Item)}
      clickOnSave={() => showPanel(ShownPanelsEnum.None)}
      clickOnCancel={() => showPanel(ShownPanelsEnum.None)}
      errorCode={'PL_EXPORT'}
    />
  );

  const renderAddNewFieldworkPeriods = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={new AddFieldworkPeriodsWindowDescription.itemType(getEditedPlanningItems())}
      windowDescription={AddFieldworkPeriodsWindowDescription}
      clickOnSave={() => {
        showPanel(ShownPanelsEnum.None);
        refresh();
      }}
      clickOnCancel={() => showPanel(ShownPanelsEnum.None)}
      errorCode={'PL184'}
    />
  );

  const openBulkAssignmentEdit = () => {
    const edited = getEditedPlanningItems();
    if (edited.length === 0) {
      ErrorMessage.SetNotification('Please select at least one planning item.');
      return;
    }
    setAssignmentEditItems(edited);
    setShownPanel(ShownPanelsEnum.PlanningAssignmentEdit);
  };

  const openAddNewFieldworkPeriods = () => {
    if (getEditedPlanningItems().length === 0) {
      ErrorMessage.SetNotification('Please select at least one planning item.');
      return;
    }
    showPanel(ShownPanelsEnum.AddNewItem);
  };

  const openMassStatusUpdate = () => {
    if (getEditedPlanningItems().length === 0) {
      ErrorMessage.SetNotification('Please select at least one planning item.');
      return;
    }
    showPanel(ShownPanelsEnum.MassStatusUpdate);
  };

  const openMultiEdit = () => {
    const edited = getEditedPlanningItems();
    if (edited.length === 0) {
      ErrorMessage.SetNotification('Please select at least one planning item.');
      return;
    }
    ErrorMessage.SetLoading(true);
    PlanningDetailsItemService.GetPlanningDetailsDatesByPricingIds(edited.map((item) => item.PricingId))
      .then((dates) => {
        ErrorMessage.SetLoading(false);
        setMultiEditItem(
          new PlanningItemMultiEditWindowDescription.itemType(
            getSelectedCountriesWithAssignmentNumbers(),
            maxNumberOfPlanningDetails(),
            dates
          )
        );
        setShownPanel(ShownPanelsEnum.MultiEdit);
      })
      .catch(() => {
        ErrorMessage.SetLoading(false);
        ErrorMessage.SetErrorMessage('PMEB68');
      });
  };

  const uploadInputRef = useRef(null);

  const downloadTemplate = () => {
    PlanningAssignmentService.DownloadAssignmentTemplate(serviceId)
      .then((blob) => saveAs(blob, `PlanningUser_${serviceId}.xlsx`))
      .catch(() => ErrorMessage.SetErrorMessage('PL_DLT'));
  };

  const onUploadFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setLoading(true);
    PlanningAssignmentService.UploadAssignments(file)
      .then((result) => {
        setLoading(false);
        if (result && result.success) {
          setRefreshPending(true);
          ErrorMessage.SetSuccess(
            `${result.appliedCount} item(s) updated. The planning grid is rebuilding in the background — it will show the new values here as soon as the rebuild finishes, and you will get a notification too.`
          );
        } else {
          const errs = (result && result.errors) || ['Upload failed.'];
          ErrorMessage.SetWarning(
            <div>
              <ul>
                {errs.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          );
        }
      })
      .catch(() => {
        setLoading(false);
        ErrorMessage.SetErrorMessage('PL_UPL');
      });
  };

  const renderButtonRow = () => (
    <ButtonRow
      colors={[
        ButtonColorsEnum.Secondary,
        ButtonColorsEnum.Secondary,
        ButtonColorsEnum.Primary,
        ButtonColorsEnum.Secondary,
        ButtonColorsEnum.Secondary,
        ButtonColorsEnum.Secondary,
        ButtonColorsEnum.Secondary,
      ]}
      labels={[
        'Multi Edit',
        'STATUS UPDATE',
        'ADD FW PERIOD',
        'EDIT USER',
        'DOWNLOAD TEMPLATE',
        'BULK USER UPLOAD',
        'EXPORT',
      ]}
      tooltips={['', '', '', '', 'User Update Template', '', '']}
      keys={[
        ShownPanelsEnum.MultiEdit,
        ShownPanelsEnum.MassStatusUpdate,
        ShownPanelsEnum.AddNewItem,
        ShownPanelsEnum.PlanningAssignmentEdit,
        ShownPanelsEnum.DownloadTemplate,
        ShownPanelsEnum.UploadAssignments,
        ShownPanelsEnum.Export,
      ]}
      icons={[
        'fa-solid fa-edit',
        'fa-solid fa-pen-alt',
        'fa-solid fa-plus-circle',
        'fa-solid fa-user-edit',
        'fa-solid fa-file-arrow-down',
        'fa-solid fa-file-arrow-up',
        'fa-solid fa-file-excel',
      ]}
      value={shownPanel}
      onClick={(key) => {
        if (key === ShownPanelsEnum.PlanningAssignmentEdit) {
          openBulkAssignmentEdit();
        } else if (key === ShownPanelsEnum.AddNewItem) {
          openAddNewFieldworkPeriods();
        } else if (key === ShownPanelsEnum.DownloadTemplate) {
          downloadTemplate();
        } else if (key === ShownPanelsEnum.UploadAssignments) {
          if (uploadInputRef.current) uploadInputRef.current.click();
        } else if (key === ShownPanelsEnum.MassStatusUpdate) {
          openMassStatusUpdate();
        } else if (key === ShownPanelsEnum.MultiEdit) {
          openMultiEdit();
        } else {
          showPanel(key);
        }
      }}
      roleKeys={[
        'user_key',
        'user_key',
        'user_key',
        'planning_assignment_edit',
        'planning_assignment_edit',
        'planning_assignment_edit',
        'user_key',
      ]}
      buttonClassNames={['', '', '', '', 'figmabutton_autowidth', 'figmabutton_autowidth', '']}
      disabled={[false, false, false, false, false, false, getEditedPlanningItems().length === 0]}
    />
  );

  const renderPanels = () => (
    <div>
      {renderButtonRow()}
      <input
        ref={uploadInputRef}
        type='file'
        accept='.xlsx'
        style={{ display: 'none' }}
        onChange={onUploadFileChange}
      />
      {shownPanel === ShownPanelsEnum.MultiEdit && multiEditItem !== null && renderMultiEditPanel()}
      {shownPanel === ShownPanelsEnum.MassStatusUpdate && renderMassStatusUpdate()}
      {shownPanel === ShownPanelsEnum.AddNewItem && renderAddNewFieldworkPeriods()}
      {shownPanel === ShownPanelsEnum.PlanningAssignmentEdit && renderAssignmentEditPanel()}
      {shownPanel === ShownPanelsEnum.Export && renderExportPanel()}
    </div>
  );

  if (loading) return <Loading />;

  return (
    <div
      key='planninglistdiv'
      className='contentwrapper'
    >
      {renderContextStrip()}
      {renderPanels()}
      <TabOfDataTable
        type={PlanningItem}
        shownPanel={shownPanel}
        serviceFunction={PlanningItemService.GetPlanningItemsByServiceId}
        convertDBItemsFunction={PlanningItemService.ConvertDBPlanningItems}
        showSearchStrip={true}
        searchItem={searchItem}
        itemList={planningItemsList}
        isFilterDisabled={true}
        changeOfItemListFunction={(itemList) => setPlanningItemsList(itemList)}
        forceClearCacheOnSearch={true}
        errorCode='PL347'
      />
    </div>
  );
}

export default PlanningList;
